import { Check, X } from 'lucide-react';
import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import styles from './button.module.css';

/**
 * DÜĞME STANDARDI — EYLEMİN TÜRÜ RENGİ BELİRLER (kullanıcı kararı 19.10.2026,
 * Claude Design `docs/design/dugme-standardi/README.md`):
 *
 *   primary        olumlu ana eylem (Onayla, Kaydet, Gönder, Yayınla, Kaydol)
 *   rowPositive    satırda tekrar eden "Onayla" (✓, açık turuncu zemin)
 *   rowNegative    satırda tekrar eden "Reddet" (✕, gri kenarlık, kırmızı metin)
 *   dangerOutline  olumsuz eylem (Reddet, İptal et, Sil) satır dışında
 *   danger         geri alınamaz son onay (kırmızı dolgu)
 *   secondary      yardımcı eylem (Filtrele, Dışa aktar, Paylaş)
 *   ghost          vazgeç, kapat, geri
 *   onBand         band zemininde ikincil eylem (yalnız `band` üstünde)
 *
 * Varyant ZORUNLUDUR: varsayılana güvenen düğme eylemin türünü söylemiyor
 * demektir (kapı: `packages/shared/src/design/dugme-standardi.guard.spec.ts`).
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'dangerOutline'
  | 'rowPositive'
  | 'rowNegative'
  | 'onBand';
export type ButtonSize = 'md' | 'sm' | 'touch';

/** `<Link>` gibi buton olmayan öğelere buton görünümü vermek için sınıf üretir. */
export function buttonClassName(
  variant: ButtonVariant,
  size: ButtonSize = 'md',
  extra?: string,
): string {
  return [styles.btn, styles[variant], size === 'md' ? '' : styles[size], extra ?? '']
    .filter(Boolean)
    .join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  /** Yükleme sırasında gösterilecek etiket (ör. "Gönderiliyor…"). */
  readonly loadingLabel?: ReactNode;
  readonly block?: boolean;
  /**
   * Lucide ikonu — etiketin solunda durur. Satır varyantları ikonunu kendisi
   * çizer (✓ / ✕): renk görmeyen kullanıcı da farkı okur, çağıran unutamaz.
   */
  readonly icon?: ReactNode;
  /**
   * KALICI DEVRE DIŞI = ODAK ALAN DEVRE DIŞI (kullanıcı kararı 23.10.2026;
   * WAI-ARIA APG, QA Blok 28 P3-4).
   *
   * Native `disabled` düğmesi odak ALMAZ: sekmeyle gezen kullanıcı düğmenin
   * varlığını da, yanındaki gerekçeyi de hiç duymaz. Ayrım şöyledir:
   *
   *   KALICI   kullanıcının bu ekranda kaldıramadığı bir durum kapatır
   *            (kredi gerekir, paket tavanı, yetki yok, tek sahip). Bu prop +
   *            `disabledReasonId` kullanılır: `aria-disabled="true"`, Tab ile
   *            odak alır, tıklama iş yapmaz, ekran okuyucu nedeni okur.
   *   GEÇİCİ   kendiliğinden kalkacak bir hâl (form gönderiliyor, yükleniyor).
   *            Native `disabled` ya da `loading` kalır; çift basışa kapalıdır.
   *
   * Formun henüz dolmaması (alan boş, seçim yok) bu kararın dışındadır ve
   * native `disabled` ile kalır.
   * Kapı: `button-disabled-reason.guard.spec.ts` (native `disabled` + neden
   * bağlantısı birlikte yazılırsa KIRILIR).
   */
  readonly softDisabled?: boolean;
  /**
   * Kalıcı devre dışının NEDENİNİ yazan, düğmenin yakınındaki görünür metnin
   * `id`si. Yalnız `softDisabled` iken `aria-describedby`a eklenir (çağıranın
   * kendi `aria-describedby`si korunur); kilit kalkınca bağlantı da kalkar.
   */
  readonly disabledReasonId?: string;
  /** React 19: `ref` sıradan prop olarak `<button>`a iner (menü düğmesine odak dönüşü). */
  readonly ref?: Ref<HTMLButtonElement>;
}

/** Satır varyantlarının sabit ikonu (spec "Kurallar": zemin, kenarlık VE ikon ayırır). */
function rowIcon(variant: ButtonVariant): ReactNode {
  if (variant === 'rowPositive') return <Check size={16} aria-hidden="true" />;
  if (variant === 'rowNegative') return <X size={16} aria-hidden="true" />;
  return null;
}

/**
 * Düğme — hâller: normal, üzerine gelince, basılı, odak, devre dışı, yükleniyor
 * (`docs/design/yeni-gorunum/components/Button/README.md`).
 * Metin dışarıdan gelir (i18n); bileşen sabit metin içermez.
 *
 * YÜKLENİYOR GENİŞLİĞİ SABİT, ETİKET KESİLMEZ (düğme standardı). Boştaki
 * yüz (ikon + etiket) ile yükleme yüzü (döner işaret + yükleme etiketi) AYNI
 * ızgara hücresine üst üste konur; düğme ikisinin GENİŞ olanı kadar yer tutar,
 * görünmeyen yüz `visibility: hidden` + `aria-hidden`dır. Böylece "Kaydol"
 * düğmesi yüklenirken "Gönderiliyor…" sığar ve düğme ne büyür ne küçülür.
 * (İlk hâlde yükleme yüzü boştaki genişliğe sıkıştırılıp üç noktayla
 * kesiliyordu: "Kaydol" → "Gön…".)
 *
 * `loadingLabel` verilmeyen düğmenin boştaki DOM'u değişmez; yükleme yüzü
 * yalnız yüklenirken eklenir (etiketi aynı metin olduğu için genişlik aynı).
 */
export function Button({
  variant,
  size = 'md',
  loading,
  loadingLabel,
  block = false,
  icon,
  className,
  disabled,
  softDisabled = false,
  disabledReasonId,
  type,
  children,
  onClick,
  'aria-busy': ariaBusy,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: ButtonProps) {
  const busy = loading === true;
  const slotIcon = rowIcon(variant) ?? icon ?? null;
  /* Yükleme etiketi boştaki etiketten uzun olabilir: genişlik ikisinin büyüğüne göre ayrılır. */
  const reserve = loadingLabel !== undefined && loadingLabel !== null;
  const describedBy =
    [ariaDescribedBy, softDisabled ? disabledReasonId : undefined].filter(Boolean).join(' ') ||
    undefined;
  return (
    <button
      {...rest}
      type={type ?? 'button'}
      /*
        Çağıranın `aria-busy`si KORUNUR: yükleme durumu yalnız düğmenin kendi
        isteğinden değil, düğmeyi KAPATACAK verinin (paket durumu) beklenmesinden
        de doğabilir. Eskiden bileşenin değeri çağıranınkini eziyordu.
      */
      aria-busy={busy || ariaBusy || undefined}
      aria-disabled={softDisabled || undefined}
      aria-describedby={describedBy}
      /*
        `type="submit"` düğmesi `aria-disabled` ile de FORMU GÖNDERİR; olay
        burada durdurulur. Aksi halde "yumuşak kilit" yalnız görsel olurdu.
      */
      onClick={softDisabled ? (formEvent) => formEvent.preventDefault() : onClick}
      disabled={disabled || busy}
      className={[
        buttonClassName(variant, size),
        busy ? styles.loading : '',
        block ? styles.block : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {busy || reserve ? (
        <span className={styles.stack}>
          <span className={busy ? styles.hiddenFace : styles.face} aria-hidden={busy || undefined}>
            {slotIcon}
            {children}
          </span>
          <span className={busy ? styles.face : styles.hiddenFace} aria-hidden={!busy || undefined}>
            <span className={busy ? styles.spinner : styles.spinnerIdle} aria-hidden="true" />
            {loadingLabel ?? children}
          </span>
        </span>
      ) : (
        <>
          {slotIcon}
          {children}
        </>
      )}
    </button>
  );
}
